<template>
  <div class="parking-public-page" :class="{ 'payment-approved-page': session && session.status === 'paid' }">
    <div class="payment-panel">
      <div class="brand">
        <img src="../../assets/images/LOGO_NEWTEC_CLOUD_ICONE_2.png" alt="Newtec" />
        <div>
          <h1>Pagamento do estacionamento</h1>
          <p v-if="!session">Escaneie o QR Code recebido na entrada ou informe a placa do veículo.</p>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger">{{ normalizeMessage(errorMessage) }}</div>
      <div v-if="successMessage" class="alert alert-success">{{ successMessage }}</div>

      <div v-if="!session">
        <div class="row">
          <div class="col-lg-7">
            <label class="form-label">Placa do veículo</label>
            <input
              v-model="plate"
              type="text"
              class="form-control plate-input"
              maxlength="8"
              placeholder="ABC1D23"
              @keyup.enter="lookupByPlate"
            />
          </div>
          <div class="col-lg-5 d-flex align-items-end">
            <button class="btn btn-primary w-100 mt-3 mt-lg-0" @click="lookupByPlate" :disabled="loading">
              Buscar valor
            </button>
          </div>
        </div>

        <div class="qr-area mt-4">
          <div class="d-flex align-items-center justify-content-between mb-2">
            <h5 class="mb-0">QR Code</h5>
            <div class="qr-actions">
              <button class="btn btn-outline-secondary btn-sm" @click="toggleScanner">
                {{ scanning ? 'Parar câmera' : 'Escanear QR Code' }}
              </button>
            </div>
          </div>

          <div v-if="scanning" class="scanner-box">
            <div class="scanner-video-wrap">
              <video ref="video" autoplay muted playsinline></video>
              <div class="scanner-target"></div>
            </div>
            <p v-if="scannerMessage" class="scanner-message mb-0 mt-2">
              {{ scannerMessage }}
            </p>
          </div>
        </div>
      </div>

      <div v-else>
        <div class="summary-box">
          <div>
            <span class="label">Placa</span>
            <strong class="plate">{{ session.plate }}</strong>
          </div>
          <div>
            <span class="label">Entrada</span>
            <strong>{{ session.entered_at }}</strong>
          </div>
          <div>
            <span class="label">Valor a pagar</span>
            <strong class="amount">{{ amountFormatted }}</strong>
          </div>
        </div>

        <div v-if="session.status === 'paid'" class="paid-box">
          Pagamento confirmado. Sua saída já está liberada.
        </div>

        <div v-if="session.status !== 'paid'">
          <div class="pix-box">
            <h5 class="mb-3">Pix</h5>
            <div v-if="loadingPix" class="text-center p-3">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 mb-0">Gerando QR Code Pix...</p>
            </div>
            <div v-else-if="pixPayment">
              <div v-if="pixPayment.qrCodeBase64" class="text-center mb-3">
                <img :src="`data:image/png;base64,${pixPayment.qrCodeBase64}`" class="pix-qr" alt="QR Code Pix" />
              </div>
              <label class="form-label">Pix cópia e cola</label>
              <textarea class="form-control pix-code" readonly :value="pixPayment.qrCode"></textarea>
              <button class="btn btn-outline-primary w-100 mt-3" @click="copyPixCode">
                Copiar código Pix
              </button>
              <p class="text-muted small mt-3 mb-0">
                A página vai verificar automaticamente quando o pagamento for aprovado.
              </p>
            </div>
            <div v-else class="text-center p-3">
              <p class="mb-2">Não foi possível gerar o Pix automaticamente.</p>
              <button class="btn btn-primary" @click="generatePixPayment" :disabled="loadingPix || loadingPayment">
                Tentar gerar QR Code Pix novamente
              </button>
            </div>
          </div>

          <div class="divider-label">
            <span>ou pague com cartão</span>
          </div>

          <div id="parking-payment-brick" class="mt-4"></div>
          <div v-if="loadingBrick" class="text-center p-3">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 mb-0">Carregando formas de pagamento...</p>
          </div>
          <div v-if="loadingPayment" class="text-center p-3">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2 mb-0">Processando pagamento...</p>
          </div>
        </div>

        <button class="btn btn-outline-secondary w-100 mt-3" @click="resetSearch" :disabled="loadingPayment">
          Buscar outra placa
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { BrowserMultiFormatReader } from '@zxing/browser';
import api from '../../config/axios.js';

export default {
  name: "ParkingPublicPayment",
  data() {
    return {
      plate: '',
      token: '',
      session: null,
      amount: 0,
      amountFormatted: '',
      mercadoPagoPublicKey: '',
      loading: false,
      loadingBrick: false,
      loadingPayment: false,
      loadingPix: false,
      errorMessage: '',
      successMessage: '',
      pixPayment: null,
      paymentStatusInterval: null,
      approvedResetTimeout: null,
      scanning: false,
      scanInterval: null,
      mediaStream: null,
      scannerMessage: '',
      scannerSessionId: 0,
      scannerFrame: null,
      barcodeDetector: null,
      zxingReader: null,
      zxingControls: null,
      lastScannedCode: '',
      lastScanAt: 0,
      brickController: null
    };
  },
  mounted() {
    const params = new URLSearchParams(window.location.search);
    this.token = params.get('token') || '';

    if (this.token) {
      this.lookup({ token: this.token });
    }
  },
  beforeUnmount() {
    this.stopScanner();
    this.destroyBrick();
    this.stopPaymentStatusPolling();
    this.stopApprovedResetTimer();
  },
  methods: {
    lookupByPlate() {
      const normalizedPlate = this.normalizePlate(this.plate);
      if (!normalizedPlate) {
        this.errorMessage = "Informe a placa do veículo.";
        return;
      }

      this.lookup({ plate: normalizedPlate });
    },
    async lookup(payload) {
      this.loading = true;
      this.errorMessage = '';
      this.successMessage = '';

      await api.axios.post('/api/parking/public/lookup', payload).then(response => {
        this.pixPayment = null;
        this.stopPaymentStatusPolling();
        this.session = response.data.session;
        this.amount = Number(response.data.amount || 0);
        this.amountFormatted = response.data.amount_formatted;
        this.mercadoPagoPublicKey = response.data.mercado_pago_public_key;
        this.stopScanner();

        this.$nextTick(() => {
          if (this.session && this.session.status !== 'paid') {
            this.renderPaymentBrick();
            this.generatePixPayment();
          } else if (this.session && this.session.status === 'paid') {
            this.scheduleApprovedReset();
          }
        });
      }).catch(error => {
        this.errorMessage = error && error.response && error.response.data
          ? error.response.data
          : "Não foi possível buscar o valor do estacionamento.";
      }).finally(() => {
        this.loading = false;
      });
    },
    async renderPaymentBrick() {
      this.destroyBrick();
      this.loadingBrick = true;

      if (!this.mercadoPagoPublicKey) {
        this.errorMessage = "Chave pública do Mercado Pago não configurada.";
        this.loadingBrick = false;
        return;
      }

      try {
        await this.loadMercadoPagoScript();
      } catch (error) {
        this.loadingBrick = false;
        this.errorMessage = "Não foi possível carregar o Mercado Pago. Verifique a conexão e tente novamente.";
        return;
      }

      const mp = new window.MercadoPago(this.mercadoPagoPublicKey, { locale: 'pt-BR' });
      const bricksBuilder = mp.bricks();

      const settings = {
        initialization: {
          amount: this.amount,
          payer: {
            email: this.systemPayerEmail()
          }
        },
        customization: {
          paymentMethods: {
            creditCard: 'all',
            debitCard: 'all',
            maxInstallments: 1
          },
          visual: {
            style: {
              theme: 'default'
            }
          }
        },
        callbacks: {
          onReady: () => {
            this.loadingBrick = false;
          },
          onSubmit: ({ formData }) => {
            return this.submitPayment(formData);
          },
          onError: (error) => {
            this.loadingBrick = false;
            this.errorMessage = error && error.message ? error.message : "Erro no formulário de pagamento.";
          }
        }
      };

      try {
        this.brickController = await bricksBuilder.create('payment', 'parking-payment-brick', settings);
      } catch (error) {
        this.loadingBrick = false;
        this.errorMessage = error && error.message ? error.message : "Erro ao carregar as formas de pagamento.";
      }
    },
    generatePixPayment() {
      if (this.pixPayment || this.loadingPix) {
        return Promise.resolve();
      }

      return this.submitPayment({
        payment_method_id: 'pix'
      }, { pixQuick: true });
    },
    async submitPayment(formData, options = {}) {
      if (options.pixQuick) {
        this.loadingPix = true;
      } else {
        this.loadingPayment = true;
      }
      this.errorMessage = '';
      this.successMessage = '';

      const payload = {
        ...formData,
        public_token: this.session.public_token,
        transaction_amount: this.amount
      };

      return api.axios.post('/api/parking/public/payment/process', payload).then(response => {
        if (response.data.status === 'approved') {
          this.successMessage = "Pagamento confirmado. Sua saída está liberada.";
          this.session.status = 'paid';
          this.destroyBrick();
          this.scheduleApprovedReset();
        } else {
          const transactionData = response.data.payment &&
            response.data.payment.point_of_interaction &&
            response.data.payment.point_of_interaction.transaction_data
              ? response.data.payment.point_of_interaction.transaction_data
              : null;

          if (transactionData && transactionData.qr_code) {
            this.pixPayment = {
              id: response.data.payment.id,
              qrCode: transactionData.qr_code,
              qrCodeBase64: transactionData.qr_code_base64 || '',
            };
            this.successMessage = "Pix gerado. Escaneie o QR Code ou use o código copia e cola para pagar.";
            this.startPaymentStatusPolling();
          } else {
            this.successMessage = "Pagamento criado. Assim que o Mercado Pago confirmar, a placa será liberada.";
          }
        }
      }).catch(error => {
        this.errorMessage = error && error.response && error.response.data
          ? error.response.data
          : "Erro ao processar pagamento.";
        return Promise.reject(error);
      }).finally(() => {
        if (options.pixQuick) {
          this.loadingPix = false;
        } else {
          this.loadingPayment = false;
        }
      });
    },
    startPaymentStatusPolling() {
      this.stopPaymentStatusPolling();

      if (!this.pixPayment || !this.pixPayment.id) {
        return;
      }

      this.paymentStatusInterval = window.setInterval(() => {
        this.syncPaymentStatus();
      }, 5000);
    },
    stopPaymentStatusPolling() {
      if (this.paymentStatusInterval) {
        clearInterval(this.paymentStatusInterval);
        this.paymentStatusInterval = null;
      }
    },
    async syncPaymentStatus() {
      if (!this.pixPayment || !this.pixPayment.id) {
        return;
      }

      await api.axios.post('/api/parking/public/payment/status', {
        public_token: this.session.public_token,
        mercado_pago_payment_id: this.pixPayment.id
      }).then(response => {
        if (response.data.status === 'approved') {
          this.session = response.data.session || this.session;
          this.session.status = 'paid';
          this.pixPayment = null;
          this.stopPaymentStatusPolling();
          this.successMessage = "Pagamento confirmado. Sua saída está liberada.";
          this.scheduleApprovedReset();
        }
      });
    },
    copyPixCode() {
      if (!this.pixPayment || !this.pixPayment.qrCode) {
        return;
      }

      navigator.clipboard.writeText(this.pixPayment.qrCode);
      this.successMessage = "Código Pix copiado.";
    },
    scheduleApprovedReset() {
      this.stopApprovedResetTimer();
      this.approvedResetTimeout = window.setTimeout(() => {
        this.resetSearch();
      }, 45000);
    },
    stopApprovedResetTimer() {
      if (this.approvedResetTimeout) {
        clearTimeout(this.approvedResetTimeout);
        this.approvedResetTimeout = null;
      }
    },
    systemPayerEmail() {
      const sessionId = this.session && this.session.id ? this.session.id : 'cliente';
      return `parking+${sessionId}@newteccloud.com`;
    },
    loadMercadoPagoScript() {
      return new Promise((resolve, reject) => {
        if (window.MercadoPago) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://sdk.mercadopago.com/js/v2';
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    },
    async toggleScanner() {
      if (this.scanning) {
        this.stopScanner();
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';
      this.scannerMessage = '';

      if (!window.isSecureContext) {
        this.errorMessage = "A câmera foi bloqueada pelo navegador. Use HTTPS ou localhost para escanear o QR Code.";
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.errorMessage = "Este navegador não permite acessar a câmera neste contexto.";
        return;
      }

      try {
        this.scannerSessionId += 1;
        const sessionId = this.scannerSessionId;
        this.scanning = true;
        this.scannerMessage = "Aponte a câmera para o QR Code recebido na entrada.";

        await this.$nextTick();

        const video = this.$refs.video;

        if (!video) {
          this.stopScanner();
          return;
        }

        if ('BarcodeDetector' in window) {
          await this.startNativeQrScanner(video, sessionId);
        } else {
          await this.startZxingQrScanner(video, sessionId);
        }
      } catch (error) {
        this.errorMessage = "Não foi possível acessar a câmera para ler o QR Code. Verifique a permissão e tente novamente.";
        this.stopScanner();
      }
    },
    async startNativeQrScanner(video, sessionId) {
      this.barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' }
        },
        audio: false
      });

      if (sessionId !== this.scannerSessionId || !this.scanning) {
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      this.mediaStream = stream;
      video.srcObject = stream;
      await video.play();
      this.scanQrFrame(sessionId);
    },
    async startZxingQrScanner(video, sessionId) {
      this.zxingReader = new BrowserMultiFormatReader();
      this.zxingControls = await this.zxingReader.decodeFromConstraints(
        {
          video: {
            facingMode: { ideal: 'environment' }
          },
          audio: false
        },
        video,
        (result) => {
          if (sessionId !== this.scannerSessionId || !this.scanning || !result) {
            return;
          }

          this.handleScannedQr(result.getText());
        }
      );

      if (sessionId !== this.scannerSessionId || !this.scanning) {
        this.stopScanner();
      }
    },
    async scanQrFrame(sessionId) {
      if (sessionId !== this.scannerSessionId || !this.scanning || !this.barcodeDetector) {
        return;
      }

      const video = this.$refs.video;

      if (!video || video.readyState < 2) {
        this.scannerFrame = requestAnimationFrame(() => this.scanQrFrame(sessionId));
        return;
      }

      try {
        const codes = await this.barcodeDetector.detect(video);

        if (codes.length > 0 && codes[0].rawValue) {
          this.handleScannedQr(codes[0].rawValue);
          return;
        }
      } catch (error) {
        this.scannerMessage = "Não foi possível ler este QR Code. Tente aproximar melhor a câmera.";
      }

      if (sessionId === this.scannerSessionId && this.scanning) {
        this.scannerFrame = requestAnimationFrame(() => this.scanQrFrame(sessionId));
      }
    },
    handleScannedQr(rawValue) {
      const code = String(rawValue || '').trim();
      const now = Date.now();

      if (!code || (code === this.lastScannedCode && now - this.lastScanAt < 1800)) {
        return;
      }

      const token = this.extractTokenFromQr(code);

      if (!token) {
        return;
      }

      this.lastScannedCode = code;
      this.lastScanAt = now;
      this.scannerMessage = "QR Code lido. Buscando pagamento...";
      this.stopScanner();
      this.lookup({ token });
    },
    stopScanner() {
      this.scannerSessionId += 1;
      this.scanning = false;
      this.barcodeDetector = null;

      if (this.scanInterval) {
        clearInterval(this.scanInterval);
        this.scanInterval = null;
      }

      if (this.scannerFrame) {
        cancelAnimationFrame(this.scannerFrame);
        this.scannerFrame = null;
      }

      if (this.zxingControls) {
        if (typeof this.zxingControls.stop === 'function') {
          this.zxingControls.stop();
        }
        this.zxingControls = null;
      }

      this.zxingReader = null;

      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
      }

      const video = this.$refs.video;

      if (video) {
        video.pause();
        if (video.srcObject) {
          video.srcObject.getTracks().forEach(track => track.stop());
        }
        video.srcObject = null;
        video.removeAttribute('src');
        video.load();
      }
    },
    extractTokenFromQr(value) {
      try {
        const url = new URL(value);
        return url.searchParams.get('token') || value;
      } catch (error) {
        return value;
      }
    },
    destroyBrick() {
      if (this.brickController && this.brickController.unmount) {
        this.brickController.unmount();
      }
      this.brickController = null;
      this.loadingBrick = false;
    },
    resetSearch() {
      this.destroyBrick();
      this.stopPaymentStatusPolling();
      this.stopApprovedResetTimer();
      this.clearPaymentUrl();
      this.session = null;
      this.token = '';
      this.plate = '';
      this.amount = 0;
      this.amountFormatted = '';
      this.pixPayment = null;
      this.successMessage = '';
      this.errorMessage = '';
    },
    clearPaymentUrl() {
      const cleanUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, cleanUrl);
    },
    normalizePlate(plate) {
      return String(plate || '').replace(/[^a-z0-9]/gi, '').toUpperCase();
    },
    normalizeMessage(message) {
      if (typeof message === 'string') {
        return message;
      }

      if (message && message.message) {
        return message.message;
      }

      return "Ocorreu um erro inesperado.";
    }
  }
};
</script>

<style scoped>
.parking-public-page {
  align-items: center;
  background: #eef4ff;
  display: flex;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  transition: background-color 0.25s ease;
  width: 100%;
}

.parking-public-page.payment-approved-page {
  background: #edf9f1;
}

.payment-panel {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 18px 42px rgba(48, 65, 101, 0.14);
  max-width: 760px;
  padding: 28px;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
  width: 100%;
}

.payment-approved-page .payment-panel {
  border: 1px solid #bfe8cd;
  box-shadow: 0 18px 42px rgba(31, 122, 65, 0.12);
}

.brand {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
  margin-bottom: 24px;
  text-align: center;
}

.brand img {
  border-radius: 12px;
  height: 76px;
  object-fit: contain;
  width: auto;
}

.brand h1 {
  color: #304165;
  font-size: 1.55rem;
  font-weight: 700;
  margin: 0;
}

.brand p {
  color: #60708f;
  margin: 4px 0 0;
}

.plate-input {
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.qr-area {
  background: #f8faff;
  border: 1px solid #e8eefb;
  border-radius: 8px;
  padding: 16px;
}

.qr-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.scanner-video-wrap {
  background: #111827;
  border-radius: 8px;
  min-height: 260px;
  overflow: hidden;
  position: relative;
}

.scanner-box video {
  background: #111827;
  display: block;
  height: 260px;
  object-fit: cover;
  width: 100%;
}

.scanner-target {
  border: 2px solid rgba(255, 255, 255, 0.9);
  border-radius: 12px;
  box-shadow: 0 0 0 999px rgba(17, 24, 39, 0.35);
  height: 150px;
  left: 50%;
  max-width: 72%;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 260px;
}

.scanner-message {
  color: #60708f;
  font-size: 0.85rem;
}

.summary-box {
  border: 1px solid #e8eefb;
  border-radius: 8px;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 16px;
}

.label {
  color: #60708f;
  display: block;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.plate {
  letter-spacing: 2px;
}

.amount {
  color: #4169e1;
  font-size: 1.35rem;
}

.paid-box {
  background: #e4f7ec;
  border: 1px solid #a8dfbc;
  border-radius: 8px;
  color: #176c38;
  font-weight: 700;
  margin-top: 20px;
  padding: 16px;
  text-align: center;
}

.pix-box {
  background: #f8faff;
  border: 1px solid #e8eefb;
  border-radius: 8px;
  margin-top: 20px;
  padding: 18px;
}

.pix-qr {
  background: #ffffff;
  border: 1px solid #e1e7f5;
  border-radius: 8px;
  max-width: 240px;
  padding: 12px;
  width: 100%;
}

.pix-code {
  font-size: 0.9rem;
  min-height: 96px;
  resize: vertical;
  word-break: break-all;
}

.quick-pix-box {
  align-items: center;
  background: #f8faff;
  border: 1px solid #e8eefb;
  border-radius: 8px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-top: 24px;
  padding: 18px;
}

.divider-label {
  align-items: center;
  color: #60708f;
  display: flex;
  gap: 12px;
  margin: 24px 0 4px;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.divider-label::before,
.divider-label::after {
  background: #e1e7f5;
  content: "";
  flex: 1;
  height: 1px;
}

:deep(#card-number-container),
:deep(#expiration-date-container),
:deep(#security-code-container) {
  overflow: hidden;
  position: relative;
}

:deep(#card-number-container iframe[name="cardNumber"]),
:deep(#expiration-date-container iframe[name="expirationDate"]),
:deep(#security-code-container iframe[name="securityCode"]) {
  display: block !important;
  height: 24px !important;
  max-height: 24px !important;
  width: 100% !important;
}

:deep(#parking-payment-brick [class*="button-container"]),
:deep(#parking-payment-brick button[type="submit"]) {
  flex: 1 1 auto !important;
  max-width: none !important;
  width: 100% !important;
}

@media (max-width: 767.98px) {
  .parking-public-page {
    padding: 12px;
  }

  .payment-panel {
    padding: 18px;
  }

  .brand {
    align-items: center;
  }

  .brand h1 {
    font-size: 1.25rem;
  }

  .summary-box {
    grid-template-columns: 1fr;
  }

  .quick-pix-box {
    align-items: stretch;
    flex-direction: column;
  }

  .qr-actions {
    justify-content: stretch;
    width: 100%;
  }

  .qr-actions .btn {
    flex: 1 1 auto;
  }
}
</style>
